import React, { useEffect, useState } from "react";
import { EditTwoTone } from "@ant-design/icons";
import {
  Button,
  Col,
  Descriptions,
  Form,
  Input,
  Row,
  Select,
  Tag,
  theme,
} from "antd";
import PracticesService, {
  ISequenceReviewInitialValues,
  ISequenceReviewFormValues,
  PracticeUpdateTypes,
} from "../../Services/PracticesService";
import { Utils } from "../../Utilities/Utils";
import { adminContext } from "../../Common/PageRoute";
import { useUser } from "../../Context/UserContext";
import { AppDrawer } from "../../Common/Layout";
import ReviewRequest from "./ReviewRequest";

interface PracticeSequenceProps {
  isEdit: { type: PracticeUpdateTypes };
  setIsEdit: React.Dispatch<
    React.SetStateAction<{ type: PracticeUpdateTypes }>
  >;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  practiceId: number;
}

const SEQUENCE_TYPES = [
  { value: 0, label: "None" },
  { value: 1, label: "Yearly" },
  { value: 2, label: "Monthly" },
];

const formatSequencePreview = (seq?: any) => {
  if (!seq) return "—";
  const typeLabel = seq.type === 1 ? "YYYY" : seq.type === 2 ? "YYYYMM" : "";
  const sep = seq.seperator || "";
  const prefix = seq.prefix || "";
  const suffix = seq.postfix || "";
  const zeros = "0".repeat(Number(seq.leadingZero) || 3) + "1";

  const parts = [prefix, typeLabel, zeros, suffix].filter(Boolean);
  return parts.join(sep) || "—";
};

const PracticeSequence: React.FC<PracticeSequenceProps> = ({
  isEdit,
  setIsEdit,
  setLoading,
  practiceId,
}) => {
  const { openNotification } = React.useContext(adminContext);
  const { isSubAdmin } = useUser();
  const isViewOnly = isSubAdmin();
  const { token } = theme.useToken();

  const [form] = Form.useForm();
  const [data, setData] = useState<ISequenceReviewFormValues | null>(null);
  const [saving, setSaving] = useState(false);

  const getSequenceDetails = async () => {
    try {
      setLoading(true);
      const res =
        await PracticesService.getPracticeSequenceReviewDetails(practiceId);
      if (res?.result && res?.status) {
        const fieldValues = {
          sequences: {
            invoice: res.result.sequences?.invoice,
            quote: res.result.sequences?.quote,
            creditNote: res.result.sequences?.creditNote,
            receipt: res.result.sequences?.receipt,
            refund: res.result.sequences?.refund,
          },
          appSubFeature: {
            reviewRequestConfig: {
              apct: res.result.reviewRequestConfig?.apct ?? false,
              sA100: res.result.reviewRequestConfig?.sA100 ?? false,
              sA800: res.result.reviewRequestConfig?.sA800 ?? false,
              vat: res.result.reviewRequestConfig?.vat ?? false,
            },
          },
        };
        setData(fieldValues);
        form.setFieldsValue(fieldValues);
      } else {
        openNotification(
          "error",
          res?.message || "Error fetching sequence details",
        );
      }
    } catch {
      openNotification("error", "Error fetching sequence details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (practiceId) {
      getSequenceDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceId]);

  const isEditing = isEdit.type === PracticeUpdateTypes.praticeSequences;

  const handleOpenEdit = () => {
    if (data) {
      form.setFieldsValue(data);
    }
    setIsEdit({ type: PracticeUpdateTypes.praticeSequences });
  };

  const handleCancel = () => {
    if (data) {
      form.setFieldsValue(data);
    }
    setIsEdit({ type: PracticeUpdateTypes.unknown });
  };

  const saveSequences = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      setLoading(true);

      const params: ISequenceReviewInitialValues = {
        invoice: values.sequences.invoice,
        quote: values.sequences.quote,
        creditNote: values.sequences.creditNote,
        receipt: values.sequences.receipt,
        refund: values.sequences.refund,
      };

      const res = await PracticesService.updatePracticeSequence(
        practiceId,
        params,
      );

      if (res?.result && res?.status) {
        openNotification("success", "Sequences updated successfully");
        setIsEdit({ type: PracticeUpdateTypes.unknown });
        await getSequenceDetails();
      } else {
        openNotification("error", res?.message || "Error updating sequences");
      }
    } catch {
      openNotification("error", "Error updating sequences");
    } finally {
      setSaving(false);
      setLoading(false);
    }
  };

  const sequenceItems = [
    {
      key: "invoice",
      label: "Invoice Sequence",
      data: data?.sequences?.invoice,
    },
    { key: "quote", label: "Quote Sequence", data: data?.sequences?.quote },
    {
      key: "creditNote",
      label: "Credit Note Sequence",
      data: data?.sequences?.creditNote,
    },
    {
      key: "receipt",
      label: "Receipt Sequence",
      data: data?.sequences?.receipt,
    },
    { key: "refund", label: "Refund Sequence", data: data?.sequences?.refund },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* 1. Practice Sequences Section */}
      <div
        style={{
          background: token.colorBgContainer,
          borderRadius: token.borderRadiusLG,
          padding: "16px 20px",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: 12,
            marginBottom: 14,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
          }}
        >
          <div>
            <span
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: token.colorTextHeading,
              }}
            >
              Practice Sequences
            </span>
            <div
              style={{
                fontSize: 11,
                color: token.colorTextSecondary,
                marginTop: 1,
              }}
            >
              Document numbering schemes and prefixes for invoices, quotes,
              credit notes, receipts, and refunds.
            </div>
          </div>

          {!isViewOnly && (
            <Button
              size="small"
              type="text"
              shape="circle"
              icon={<EditTwoTone />}
              onClick={handleOpenEdit}
            />
          )}
        </div>

        <Descriptions
          size="small"
          bordered={false}
          column={{ xs: 1, sm: 2, md: 3 }}
          labelStyle={{
            color: token.colorTextSecondary,
            fontWeight: 500,
            fontSize: 12,
          }}
          contentStyle={{
            color: token.colorText,
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          {sequenceItems.map((seq) => {
            const typeText =
              seq.data?.type === 1
                ? "Yearly"
                : seq.data?.type === 2
                  ? "Monthly"
                  : "None";
            return (
              <Descriptions.Item key={seq.key} label={seq.label}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Tag
                    color="blue"
                    style={{ borderRadius: 4, margin: 0, fontWeight: 600 }}
                  >
                    {formatSequencePreview(seq.data)}
                  </Tag>
                  <span
                    style={{ fontSize: 11, color: token.colorTextTertiary }}
                  >
                    ({typeText})
                  </span>
                </div>
              </Descriptions.Item>
            );
          })}
        </Descriptions>
      </div>

      {/* 2. Review Request Section */}
      <ReviewRequest
        isEdit={isEdit}
        setIsEdit={setIsEdit}
        practiceId={practiceId}
        data={data?.appSubFeature?.reviewRequestConfig}
        onUpdated={getSequenceDetails}
      />

      {/* Edit Practice Sequences Drawer */}
      <AppDrawer
        title="Edit Practice Sequences"
        subtitle="Configure numbering rules, separators, prefixes and leading zeros"
        width={560}
        open={isEditing}
        onClose={handleCancel}
        onSave={saveSequences}
        saveText="Save Changes"
        saveButtonProps={{ loading: saving }}
        cancelButtonProps={{ disabled: saving }}
      >
        <Form form={form} layout="vertical" disabled={saving}>
          {sequenceItems.map((seq) => (
            <div
              key={seq.key}
              style={{
                marginBottom: 16,
                padding: 12,
                borderRadius: token.borderRadius,
                background: token.colorFillAlter,
                border: `1px solid ${token.colorBorderSecondary}`,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: token.colorText,
                }}
              >
                {seq.label}
              </div>

              <Row gutter={8}>
                <Col span={8}>
                  <Form.Item
                    label="Type"
                    name={["sequences", seq.key, "type"]}
                    style={{ marginBottom: 8 }}
                  >
                    <Select options={SEQUENCE_TYPES} placeholder="Type" />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    label="Prefix"
                    name={["sequences", seq.key, "prefix"]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input
                      onKeyPress={(e) => Utils.ValidateCharAndNumberOnly(e)}
                      placeholder="e.g. INV"
                    />
                  </Form.Item>
                </Col>

                <Col span={8}>
                  <Form.Item
                    label="Separator"
                    name={["sequences", seq.key, "seperator"]}
                    style={{ marginBottom: 8 }}
                  >
                    <Input placeholder="e.g. -" maxLength={3} />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Leading Zeros"
                    name={["sequences", seq.key, "leadingZero"]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      onKeyPress={(e) => Utils.ValidateNumberOnly(e)}
                      placeholder="e.g. 4"
                    />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label="Suffix"
                    name={["sequences", seq.key, "postfix"]}
                    style={{ marginBottom: 0 }}
                  >
                    <Input
                      onKeyPress={(e) => Utils.ValidateCharAndNumberOnly(e)}
                      placeholder="e.g. /UK"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </div>
          ))}
        </Form>
      </AppDrawer>
    </div>
  );
};

export default PracticeSequence;
